"use client";

import { useCounterZone } from "@/lib/time/counterZone";
import { useCallback, useEffect, useState, useSyncExternalStore } from "react";
import { IntlProvider, useLocale } from "use-intl";
import { DEFAULT_LOCALE, htmlLang, normaliseLocale, resolveLocale, type CounterLocale } from "@/lib/i18n/config";
import { MESSAGES } from "@/lib/i18n/messages";
import {
  readDeviceLocale,
  readStaffLocale,
  subscribeStaffLocale,
  writeDeviceLocale,
  writeStaffLocale,
} from "@/lib/i18n/staff-locale";
import { fetchStaffPreference, saveStaffPreference } from "@/lib/i18n/staff-preference";
import { usePosSession } from "@/lib/hooks/usePosSession";

const serverNone = () => undefined;

/** Staff ids whose profile has been read in this page load, so a remount does not ask again. */
const profileRead = new Set<string>();

/**
 * The counter's language, for every screen under the root layout.
 *
 * Precedence is staff > device > business: the signed-in person's profile choice (cached on this
 * device per staff id), then this till's own choice, then `pos.locale`. The server renders English
 * (it cannot see `/pos/context` or this device's storage), and the client moves to the resolved
 * language once they are known — the same server-snapshot pattern `useVocab` and the rail use. A
 * page that never signs in (the counter sign-in) stays English and renders exactly as it did: the
 * provider adds no element and no style.
 *
 * Once a staff id is known the profile is read once; its answer replaces the cache, so a choice made
 * on another till arrives here, and a choice cleared there is cleared here.
 *
 * `onError` is quiet on purpose. A missing key renders its dotted path, which the catalog gate
 * (`scripts/check-i18n.ts`) is there to prevent; logging it on every render of a till would bury the
 * console the next time somebody needs it for something real.
 */
export function CounterIntlProvider({ children }: { children: React.ReactNode }) {
  const { staff, locale: businessDefault } = usePosSession();
  const staffId = staff?.id;
  const staffChoice = useSyncExternalStore(
    subscribeStaffLocale,
    useCallback(() => readStaffLocale(staffId), [staffId]),
    serverNone,
  );
  const deviceChoice = useSyncExternalStore(subscribeStaffLocale, readDeviceLocale, serverNone);
  const locale = resolveLocale(staffChoice, businessDefault, deviceChoice);
  const timeZone = useCounterZone();

  useEffect(() => {
    if (!staffId || profileRead.has(staffId)) return;
    profileRead.add(staffId);
    void fetchStaffPreference().then((fromProfile) => {
      if (fromProfile === undefined) return; // unreachable: the cache stands
      if (fromProfile !== readStaffLocale(staffId)) writeStaffLocale(staffId, fromProfile ?? undefined);
    });
  }, [staffId]);

  useEffect(() => {
    document.documentElement.lang = htmlLang(locale);
  }, [locale]);

  return (
    <IntlProvider
      locale={locale}
      messages={MESSAGES[locale]}
      timeZone={timeZone}
      onError={() => {}}
    >
      {children}
    </IntlProvider>
  );
}

/** Where the last personal choice was saved: nowhere yet, the profile, or only this till. */
export type SaveOutcome = "idle" | "saving" | "profile" | "device-only";

/**
 * What the language switchers need: the current language, where it came from, and setters.
 *
 * `set(undefined)` returns this person to the till's (or business's) language, on their profile too.
 */
export function useCounterLocale(): {
  locale: CounterLocale;
  businessDefault: CounterLocale;
  staffChoice: CounterLocale | undefined;
  deviceChoice: CounterLocale | undefined;
  /** The language this person gets when they have no choice of their own. */
  tillLanguage: CounterLocale;
  canChoose: boolean;
  saveOutcome: SaveOutcome;
  set: (next: CounterLocale | undefined) => void;
  setDevice: (next: CounterLocale | undefined) => void;
} {
  const locale = useLocale();
  const { staff, locale: served } = usePosSession();
  const staffId = staff?.id;
  const [saveOutcome, setSaveOutcome] = useState<SaveOutcome>("idle");
  const staffChoice = useSyncExternalStore(
    subscribeStaffLocale,
    useCallback(() => readStaffLocale(staffId), [staffId]),
    serverNone,
  );
  const deviceChoice = useSyncExternalStore(subscribeStaffLocale, readDeviceLocale, serverNone);
  const businessDefault = normaliseLocale(served) ?? DEFAULT_LOCALE;
  return {
    locale,
    businessDefault,
    staffChoice,
    deviceChoice,
    tillLanguage: deviceChoice ?? businessDefault,
    canChoose: Boolean(staffId),
    saveOutcome,
    set: (next) => {
      if (!staffId) return;
      writeStaffLocale(staffId, next);
      setSaveOutcome("saving");
      void saveStaffPreference(next ?? null).then((saved) => setSaveOutcome(saved ? "profile" : "device-only"));
    },
    setDevice: (next) => writeDeviceLocale(next),
  };
}
